import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, X as XIcon, PoundSterling, BarChart3, ShieldCheck, Eye, Users, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import PageHeader from '../components/PageHeader';
import PageFooter from '../components/PageFooter';
import HeadlineAccent from '../components/HeadlineAccent';
import RadialOrbitalTimeline from '../components/RadialOrbitalTimeline';

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-40px' },
    transition: { duration: 0.5 }
};

const stagger = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
};

const CORE_CARDS = [
    {
        icon: Eye,
        title: 'Talent Cards',
        desc: 'See real performance insight from AI-generated profiles — problem solving, execution, collaboration — not a list of buzzwords.',
        span: 'md:col-span-8',
    },
    {
        icon: Layers,
        title: 'Compare side-by-side',
        desc: 'Expand and compare candidates with detailed scores, work style indicators, and trial readiness — all in one view.',
        span: 'md:col-span-4 md:row-span-2',
    },
    {
        icon: ShieldCheck,
        title: 'Paid trials',
        desc: 'Test candidates in your real work environment for 1-10 days. See exactly how they perform before making any commitment.',
        span: 'md:col-span-4',
    },
    {
        icon: PoundSterling,
        title: 'Pay only if you hire',
        desc: 'No retainers, no percentage fees. You only pay a flat £750 conversion fee if you decide to bring someone on permanently.',
        span: 'md:col-span-4',
    },
];

const STEPS = [
    { num: '01', title: 'Create a role + select key skills', desc: 'Post your role with the specific skills and attributes you are looking for. Our AI uses this to find the best-fit candidates.' },
    { num: '02', title: 'Discover and compare candidates', desc: 'Browse AI-matched Talent Cards with expandable profiles. Compare candidates side-by-side on performance metrics.' },
    { num: '03', title: 'Run a paid trial', desc: 'Invite your top picks to a paid trial shift — flexible from 1 to 10 days. Everything is handled through the platform.' },
    { num: '04', title: 'Evaluate real performance', desc: 'Use our structured 6-dimension feedback form to assess how candidates actually performed in your work environment.' },
    { num: '05', title: 'Hire based on evidence', desc: 'Make confident hiring decisions backed by real trial data, not interview impressions.' },
];

const COMPARISON = [
    { dimension: 'Candidate evaluation', traditional: 'CV screening & interviews', platform: 'AI Talent Cards + real trial data' },
    { dimension: 'Decision basis', traditional: 'Gut feeling & conversation', platform: 'Structured performance scores' },
    { dimension: 'Cost', traditional: '15-25% of salary (recruiters)', platform: '£200/day + £750 on hire' },
    { dimension: 'Risk', traditional: 'High — no proof of performance', platform: 'Low — hire after seeing real work' },
    { dimension: 'Time to hire', traditional: 'Weeks to months', platform: 'Days to weeks' },
];

const FAQ_ITEMS = [
    { q: 'How does pricing work?', a: 'You pay £200 per candidate per trial day. This covers the platform fee and candidate compensation. If you decide to hire, there is a one-time £750 conversion fee. No retainers, no percentage-based commissions.' },
    { q: 'What happens if the trial does not work out?', a: 'No problem — you only pay for the days trialled. There is no conversion fee unless you make an offer that is accepted. You can also end a trial early if needed.' },
    { q: 'How are candidates matched to my role?', a: 'Our AI analyses your role requirements against candidate Talent Cards — matching on skills, performance scores, work style, and sector experience to surface the best fits.' },
    { q: 'Can I run trials for multiple candidates?', a: 'Absolutely. You can run trials for as many candidates as you like, either simultaneously or sequentially. Each trial is booked and managed independently.' },
    { q: 'What sectors are covered?', a: 'We cover 9 office-based sectors: Property, Tech & Product, Sales & Account Management, Creative & Events, HR & People, Marketing & Growth, Operations & Admin, Finance & Accounting, and Legal & Compliance — with 112 specific roles across them.' },
];

const ForEmployers = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Sampler | For Employers</title>
                <meta name="description" content="See how candidates actually perform before you hire them. Flat pricing, up to 80% cheaper than recruiters." />
            </Helmet>
            <PageHeader />

            {/* Hero */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-24 px-6 md:px-12 max-w-7xl mx-auto" data-testid="employer-hero">
                <motion.div {...fadeUp} className="text-left">
                    <span
                        className="inline-block uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] mb-6"
                        style={{ fontFamily: 'Unbounded, sans-serif' }}
                        data-testid="employer-hero-label"
                    >
                        For Employers
                    </span>
                    <h1
                        className="text-5xl sm:text-6xl font-bold tracking-tight leading-none mb-6"
                        style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}
                        data-testid="employer-hero-heading"
                    >
                        Hire based on{' '}
                        <HeadlineAccent>proof</HeadlineAccent>{' '}
                        — not CVs.
                    </h1>
                    <p
                        className="text-base md:text-lg leading-relaxed text-slate-700 mb-3 max-w-2xl"
                        style={{ fontFamily: 'Barlow, sans-serif' }}
                        data-testid="employer-hero-sub"
                    >
                        See how candidates actually perform before you hire them.
                    </p>
                    <p
                        className="text-sm font-semibold text-[#8B5CF6] mb-2"
                        style={{ fontFamily: 'Unbounded, sans-serif' }}
                        data-testid="employer-hero-pricing-line"
                    >
                        Simple, flat pricing — up to 80% cheaper than recruiters
                    </p>
                    <p className="text-sm text-slate-500 mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        No hire = no conversion fee
                    </p>
                    <Button
                        className="btn-primary text-lg h-14 px-10"
                        onClick={() => navigate('/register?type=company')}
                        data-testid="employer-hero-cta"
                    >
                        Start Hiring Smarter
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                >
                    <div className="w-full" data-testid="employer-hero-visual">
                        {/* Desktop: animated orbital timeline */}
                        <div className="hidden md:flex items-center justify-center w-full min-h-[400px]">
                            <RadialOrbitalTimeline />
                        </div>
                        {/* Mobile fallback: simple stacked list of the six dimensions */}
                        <ul className="md:hidden flex flex-col gap-3 max-w-sm mx-auto" data-testid="employer-hero-mobile-list">
                            {[
                                'Execution & Delivery',
                                'Communication',
                                'Problem Solving',
                                'Ownership & Initiative',
                                'Learning Speed',
                                'Collaboration'
                            ].map((dim) => (
                                <li
                                    key={dim}
                                    className="rounded-2xl px-5 py-3 bg-white/95 border border-purple-200/60 text-sm font-semibold text-slate-800 shadow-sm"
                                    style={{ fontFamily: 'Barlow, sans-serif' }}
                                >
                                    {dim}
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>
            </section>

            {/* Core Difference — Bento Grid */}
            <section className="py-24 md:py-32" data-testid="employer-core-difference">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            The Core Difference
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            A smarter way to hire
                        </h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-auto">
                        {CORE_CARDS.map((card, i) => {
                            return (
                                <motion.div
                                    key={i}
                                    className={`bg-[#F3E8FF] rounded-3xl p-8 border border-purple-200/50 shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-300 ${card.span}`}
                                    {...stagger}
                                    transition={{ duration: 0.45, delay: i * 0.1 }}
                                    data-testid={`employer-core-card-${i}`}
                                >
                                    <h3 className="mb-3" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '20px', fontWeight: 700, color: '#0A0A0A' }}>
                                        {card.title}
                                    </h3>
                                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                        {card.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="employer-how-it-works">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            How It Works
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            From posting to hiring in five steps
                        </h2>
                        <p className="text-slate-500 mt-3 text-base" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            No unnecessary interviews. No expensive mistakes.
                        </p>
                    </motion.div>
                    <div className="space-y-0">
                        {STEPS.map((step, i) => (
                            <motion.div
                                key={i}
                                className="flex items-start gap-6 md:gap-10 py-8 border-b border-purple-200/40 last:border-b-0"
                                {...stagger}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                                data-testid={`employer-step-${i}`}
                            >
                                <span
                                    className="text-3xl md:text-4xl font-bold text-[#8B5CF6]/30 shrink-0 w-16 text-right"
                                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                                >
                                    {step.num}
                                </span>
                                <div>
                                    <h3 className="text-lg md:text-xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                        {step.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="py-24 md:py-32" data-testid="employer-pricing">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Pricing
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Simple, transparent, fair
                        </h2>
                    </motion.div>
                    <motion.div
                        {...fadeUp}
                        className="rounded-3xl p-10 md:p-14"
                        style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
                            <div className="text-center md:text-left">
                                <p className="uppercase tracking-[0.2em] text-xs font-semibold text-purple-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Per trial day
                                </p>
                                <div className="text-5xl md:text-6xl font-bold text-[#34D399]" style={{ fontFamily: 'Unbounded, sans-serif' }} data-testid="employer-pricing-daily">
                                    £200
                                </div>
                                <p className="text-purple-200 text-sm mt-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    per candidate per day
                                </p>
                            </div>
                            <div className="text-center md:text-left">
                                <p className="uppercase tracking-[0.2em] text-xs font-semibold text-purple-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Conversion fee
                                </p>
                                <div className="text-5xl md:text-6xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }} data-testid="employer-pricing-hire">
                                    £750
                                </div>
                                <p className="text-purple-200 text-sm mt-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    only if you hire
                                </p>
                            </div>
                            <div className="space-y-3">
                                {['No retainers', 'No percentage fees', 'No hidden costs', 'Flexible trial duration'].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <CheckCircle className="h-5 w-5 text-[#34D399] shrink-0" strokeWidth={2.5} />
                                        <span className="text-purple-100 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Comparison Table */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="employer-comparison">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-12">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Comparison
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Traditional recruitment vs Sampler
                        </h2>
                    </motion.div>
                    <motion.div {...fadeUp} className="bg-white rounded-3xl border border-purple-200/50 overflow-hidden shadow-sm overflow-x-auto">
                        <div className="grid grid-cols-3 gap-0 min-w-[640px]">
                            {/* Header row */}
                            <div className="p-4 md:p-5 border-b border-r border-purple-200/50" />
                            <div className="p-4 md:p-5 border-b border-r border-purple-200/50 text-center">
                                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-400" style={{ fontFamily: 'Unbounded, sans-serif' }}>Traditional</span>
                            </div>
                            <div className="p-4 md:p-5 border-b border-purple-200/50 text-center" style={{ backgroundColor: 'rgba(243, 232, 255, 0.5)' }}>
                                <span className="uppercase tracking-[0.15em] text-xs font-semibold text-[#8B5CF6]" style={{ fontFamily: 'Unbounded, sans-serif' }}>Sampler</span>
                            </div>
                            {/* Data rows */}
                            {COMPARISON.map((row, i) => (
                                <React.Fragment key={i}>
                                    <div className={`p-4 md:p-5 ${i < COMPARISON.length - 1 ? 'border-b' : ''} border-r border-purple-200/50`}>
                                        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Barlow, sans-serif' }}>{row.dimension}</span>
                                    </div>
                                    <div className={`p-4 md:p-5 ${i < COMPARISON.length - 1 ? 'border-b' : ''} border-r border-purple-200/50 text-center`}>
                                        <span className="text-sm text-slate-500 flex items-center justify-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                            <XIcon className="h-4 w-4 text-red-400 shrink-0" strokeWidth={2.5} />
                                            <span>{row.traditional}</span>
                                        </span>
                                    </div>
                                    <div className={`p-4 md:p-5 ${i < COMPARISON.length - 1 ? 'border-b' : ''} border-purple-200/50 text-center`} style={{ backgroundColor: 'rgba(243, 232, 255, 0.5)' }}>
                                        <span className="text-sm text-[#0A0A0A] font-medium flex items-center justify-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                            <CheckCircle className="h-4 w-4 text-[#10B981] shrink-0" strokeWidth={2.5} />
                                            <span>{row.platform}</span>
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="employer-faq">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-12">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            FAQ
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Common questions
                        </h2>
                    </motion.div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQ_ITEMS.map((faq, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} className="border-purple-200/50" data-testid={`employer-faq-item-${i}`}>
                                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline py-5" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                    {faq.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    {faq.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 md:py-32" data-testid="employer-final-cta">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <motion.div {...fadeUp}>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Stop guessing. Start seeing.
                        </h2>
                        <p className="text-lg text-slate-600 mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Join the employers building a better way to hire.
                        </p>
                        <Button
                            className="btn-primary text-lg h-14 px-10"
                            onClick={() => navigate('/register?type=company')}
                            data-testid="employer-final-cta-btn"
                        >
                            Start Hiring Smarter
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </motion.div>
                </div>
            </section>

            <PageFooter />
        </div>
    );
};

export default ForEmployers;
